import { useState, useEffect } from 'react';
import { fetchPageContent } from '../lib/cms';
import { companyInfo } from '../data';
import useMeta from '../hooks/useMeta';
import ContactForm from '../components/ContactForm';
import Map from '../components/Map';

export default function Kontakt() {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      const data = await fetchPageContent('kontakt');
      setPage(data);
      setLoading(false);
    }
    loadData();
  }, []);

  useMeta(page?.meta_title, page?.meta_description);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <i className="fa-light fa-spinner-third animate-spin text-3xl text-gold-500" />
          <p className="text-sm font-medium text-slate-500">Henter indhold...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <section className="bg-slate-50/40 py-20 md:py-28 border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-500 block mb-3">
              Kontakt os
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium font-display text-navy-950 tracking-tight mb-6">
              {page.hero_title}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              {page.hero_text}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left Column: Contact Info & Map */}
            <div className="lg:col-span-5 flex flex-col gap-10">
              <div className="prose prose-slate max-w-none">
                <h2 className="font-display text-2xl font-medium text-navy-950 mb-6">
                  Vores Hovedkontor
                </h2>
                <p className="text-slate-600 leading-relaxed mb-8">
                  Vi holder til i det innovative erhvervsområde i Aalborg Øst, tæt på Aalborg Universitet og motorvejen. Du er altid velkommen til at kontakte os for at aftale et møde.
                </p>

                {/* Contact Details Cards */}
                <div className="space-y-6">
                  {/* Address */}
                  <div className="flex gap-4 items-start p-5 border border-slate-100/80 bg-slate-50/20 hover:border-gold-300/40 hover:bg-white transition-all duration-300">
                    <div className="w-10 h-10 bg-gold-50/50 flex items-center justify-center text-gold-500 border border-gold-100/40 shrink-0">
                      <i className="fa-light fa-location-dot" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-navy-950 mb-1">Adresse</h4>
                      <p className="text-sm text-slate-600">{companyInfo.address}</p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex gap-4 items-start p-5 border border-slate-100/80 bg-slate-50/20 hover:border-gold-300/40 hover:bg-white transition-all duration-300">
                    <div className="w-10 h-10 bg-gold-50/50 flex items-center justify-center text-gold-500 border border-gold-100/40 shrink-0">
                      <i className="fa-light fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-navy-950 mb-1">Telefon</h4>
                      <p className="text-sm">
                        <a 
                          href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`} 
                          className="text-slate-600 hover:text-gold-500 transition-colors duration-300 whitespace-nowrap focus:outline-none focus:underline"
                        >
                          {companyInfo.phone}
                        </a>
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex gap-4 items-start p-5 border border-slate-100/80 bg-slate-50/20 hover:border-gold-300/40 hover:bg-white transition-all duration-300">
                    <div className="w-10 h-10 bg-gold-50/50 flex items-center justify-center text-gold-500 border border-gold-100/40 shrink-0">
                      <i className="fa-light fa-envelope" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-navy-950 mb-1">E-mail</h4>
                      <p className="text-sm">
                        <a 
                          href={`mailto:${companyInfo.email}`} 
                          className="text-slate-600 hover:text-gold-500 transition-colors duration-300 whitespace-nowrap focus:outline-none focus:underline"
                        >
                          {companyInfo.email}
                        </a>
                      </p>
                    </div>
                  </div>

                  {/* Bank */}
                  <div className="flex gap-4 items-start p-5 border border-slate-100/80 bg-slate-50/20 hover:border-gold-300/40 hover:bg-white transition-all duration-300">
                    <div className="w-10 h-10 bg-gold-50/50 flex items-center justify-center text-gold-500 border border-gold-100/40 shrink-0">
                      <i className="fa-light fa-building-columns" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-navy-950 mb-1">Bank</h4>
                      <p className="text-sm text-slate-600">{companyInfo.bank}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Integrated Map */}
              <div className="shadow-sm">
                <Map />
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
